import i18n from '@risksmart-app/i18n/src/i18n';
import type { GetPolicyAttestationRecordsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  Attestation_Record_Status_Enum,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo } from 'react';
import {
  useGetAttestationsRegister,
  type UseGetAttestationsRegisterArgs,
} from 'src/hooks/queries/attestations/useGetAttestationsRegister';
import { useGetAttestationSmartWidgetTableProps } from 'src/pages/policy/update/tabs/files/update/tabs/attestations/config';
import type { AttestationRegisterFields } from 'src/pages/policy/update/tabs/files/update/tabs/attestations/types';

import { toIsoBounds } from '../../gigawidget/util/filterHelpers';
import { useMyItemsFilters } from '../../useDashboardStore';
import { createDataSource } from '../createDataSource';
import { dashboardDateRangeClickthroughFilter } from '../dataSourceHelpers';

export default createDataSource<
  AttestationRegisterFields,
  UseGetAttestationsRegisterArgs,
  GetPolicyAttestationRecordsQuery
>({
  group: 'single',
  parentTypes: [Parent_Type_Enum.AttestationRecord],
  hasAccess: (_, isModuleEnabled) =>
    isModuleEnabled('document.subModules.attestation'),
  useWidgetQuery: useGetAttestationsRegister,
  useTablePropsHook: (data, options) =>
    useGetAttestationSmartWidgetTableProps(data?.attestation_record, options),
  /**
   * The `pending` half of the old descriptor's ownership clause has no
   * server-side argument on either leg (COI-600 §4.3), so it is applied here.
   * Gated on `allowOwnershipFiltering` AND `myItemsFilters.owner` — matching
   * the old code's `if (myItemsFilters.owner) { ...pending-filtered... }`
   * clause — so it fires in exactly the cases it fired in before, and now on
   * BOTH legs, where the GraphQL leg was the only one narrowing before.
   * Attestation row counts are per-user and small.
   */
  useInitialDataFilter: (data, allowOwnershipFiltering) => {
    const { owner } = useMyItemsFilters();

    return useMemo(() => {
      if (!data || !allowOwnershipFiltering || !owner) {
        return data;
      }

      return {
        ...data,
        attestation_record: data.attestation_record.filter(
          (record) =>
            record.AttestationStatus === Attestation_Record_Status_Enum.Pending
        ),
      };
    }, [data, allowOwnershipFiltering, owner]);
  },
  dashboardFilterConfig: {
    dateFilter: (dateRange, precision) => ({
      dateRange: {
        field: 'CreatedAtTimestamp',
        ...toIsoBounds(dateRange, precision),
      },
    }),
    // Attestations belong to individual users — scope to the logged-in user.
    // `userId` REPLACES the entity filter in the hook (`base: userId ?
    // userWhere : whereFilter`), matching the descriptor, whose old
    // `useDefaultVariables` was `{ where: {} }` — so no entity filter is lost.
    ownershipFilter: (myItemsFilters, userId) =>
      myItemsFilters.owner ? { userId } : {},
    dateClickthroughFilter:
      dashboardDateRangeClickthroughFilter('CreatedAtTimestamp'),
  },
  entityNamePlural: 'attestation_other',
  entityNameSingular: 'attestation_one',
  fields: 'policy.fields',
  categoryGetters: [
    {
      id: 'status',
      name: () => i18n.t('columns.status'),
      categoryGetter: (data) => ({
        key: data.AttestationStatus,
        label: data.AttestationStatusLabel,
      }),
      ratingColourKey: 'document_file_status',
    },
    {
      id: 'user',
      name: () => i18n.t('columns.user'),
      categoryGetter: (data) => data.User,
    },
    {
      id: 'document',
      name: () => i18n.t('document_one'),
      categoryGetter: (data) => data.Document,
    },
    {
      id: 'active',
      name: () => i18n.t('active'),
      categoryGetter: (data) => data.ActiveLabel,
    },
    {
      id: 'attestedDate',
      name: () => i18n.t('columns.attested_at'),
      categoryGetter: (data) =>
        data.AttestedAt ? new Date(data.AttestedAt) : null,
      date: true,
      dashboardDateFilterOverride: (dateRange, precision) => ({
        dateRange: {
          field: 'AttestedAt',
          ...toIsoBounds(dateRange, precision),
        },
      }),
    },
    {
      id: 'expiresDate',
      name: () => i18n.t('columns.expires_at'),
      categoryGetter: (data) =>
        data.ExpiresAt ? new Date(data.ExpiresAt) : null,
      date: true,
      dashboardDateFilterOverride: (dateRange, precision) => ({
        dateRange: { field: 'ExpiresAt', ...toIsoBounds(dateRange, precision) },
      }),
    },
    {
      id: 'createdDate',
      name: () => i18n.t('columns.created_on'),
      categoryGetter: (data) => new Date(data.CreatedAtTimestamp),
      date: true,
      dashboardDateFilterOverride: (dateRange, precision) => ({
        dateRange: {
          field: 'CreatedAtTimestamp',
          ...toIsoBounds(dateRange, precision),
        },
      }),
    },
  ],
});
